/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { FloatingPanel, FloatingPanelContent, TFloatingPanelComponent } from "../FloatingPanel";
import { FloatingPanelExpressionEditorModel } from "./FloatingPanelExpressionEditorModel";
import { useFloatingPanelModel } from "../../hooks/useFloatingPanelModel";
import { CodeEditor, ICodeEditorProps } from "@adapptio/lib-editor-ui";
import { ErrorList } from "../ErrorList/ErrorList";
import { ISchemaConstStringModel, ISchemaExpressionModel } from "@adapptio/lib-blueprint";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { useBlueprintModel } from "../../hooks/useBlueprintModel";

export const FloatingPanelExpressionEditor: TFloatingPanelComponent<FloatingPanelExpressionEditorModel> = ({
	floatingPanelModel
}) => {
	const baseClass = BEM("edt-floating-panel-expression-editor");

	const { params, close, rect, setRect } = useFloatingPanelModel(floatingPanelModel);
	const {
		model: _model,
		readOnly,
		debounceTimeout,
		title,
		language,
		showStringValue,
		onOuterReferenceClick
	} = params;

	const { model } = useBlueprintModel(_model, _model.schema.name);

	const [ expLastValue, setExpLastValue ] = useState<{ result: unknown }>(
		model?.expLastValue as { result: unknown }
	);

	const resultData = showStringValue ? model.value : expLastValue?.result;

	const parseErrors = model.expParseErrors;

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model as ISchemaConstStringModel & ISchemaExpressionModel, value, true);
		},
		[ model ]
	);

	const editorProps: Partial<ICodeEditorProps> = {
		value: model.value,
		onChange: handleChange,
		language,
		parseErrors,
		// @TODO Inspector context
		readOnly,
		debounceTimeout
	};

	if (params?.className) {
		baseClass.add(params.className);
	}

	useEffect(() => {
		if (model) {
			const handleLastValueChange = () => setExpLastValue(model.expLastValue as { result: unknown });

			onEvent(model.expLastValueChangeEvent, handleLastValueChange);

			return () => {
				offEvent(model.expLastValueChangeEvent, handleLastValueChange);
			};
		}
	}, [ model ]);

	return (
		<FloatingPanel onClose={close} title={title} rect={rect} onRectChange={setRect}>
			<FloatingPanelContent className={baseClass.toClassName()}>
				<div className={baseClass.element("code-editor").toClassName()}>
					<CodeEditor
						options={{
							minimap: { enabled: false },
							scrollBeyondLastLine: false
						}}
						onOuterReferenceClick={onOuterReferenceClick}
						onEscKeyPressed={close}
						shouldFocus={true}
						{...editorProps}
					/>
				</div>
				{resultData !== undefined ? (
					<div className={baseClass.element("result-data").toClassName()}>
						<CodeEditor
							value={JSON.stringify(resultData) || ""}
							language="json"
							parseErrors={parseErrors}
							options={{
								minimap: { enabled: false },
								scrollBeyondLastLine: false,
								scrollBeyondLastColumn: 0,
								automaticLayout: true,
								tabSize: 2,
								lineNumbers: "off",
								lineDecorationsWidth: 0,
								wordWrap: "on",
								readOnly: true,
								renderLineHighlightOnlyWhenFocus: true
							}}
						/>
					</div>
				) : null}
				{
					// (validationErrors && validationErrors.length > 0) ||
					parseErrors && parseErrors.length > 0 ? (
						<div className={baseClass.element("errors").toClassName()}>
							<ErrorList
								errorGroups={[
									{
										documentUri: null,
										parseErrors: parseErrors,
										runtimeErrors: [],
										compileErrors: []
									}
								]}
							/>
						</div>
					) : null
				}
			</FloatingPanelContent>
		</FloatingPanel>
	);
};
